import "./Dropdown.css";
import {
	type Component,
	createEffect,
	createMemo,
	createSignal,
	For,
	type JSX,
	onCleanup,
	type ParentComponent,
	Show,
} from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import { Appearance } from "./Appearance";
import { useAnchoredPopupPosition } from "./hooks/useAnchoredPopupPosition";
import { useSelectOnlyCombobox } from "./hooks/useSelectOnlyCombobox";
import { MaterialSymbol } from "./MaterialSymbol";
import { Tooltip, TooltipTrigger } from "./Tooltip";

export type DropdownPlacement =
	| "bottom"
	| "bottom left"
	| "bottom right"
	| "bottom start"
	| "bottom end"
	| "top"
	| "top left"
	| "top right"
	| "top start"
	| "top end"
	| "left"
	| "left top"
	| "left bottom"
	| "start"
	| "start top"
	| "start bottom"
	| "right"
	| "right top"
	| "right bottom"
	| "end"
	| "end top"
	| "end bottom";

export type DropdownOption = {
	value: string | number;
	label: string;
	disabled?: boolean;
	symbol?: string;
	description?: string;
	/** Shown after the label when set (e.g. effort credit cost). */
	cost?: string | null;
	theme?: "default" | "danger";
};

export type DividerOption = {
	isDivider: true;
};

const isDivider = (
	option: DropdownOption | DividerOption,
): option is DividerOption => {
	return (option as DividerOption).isDivider;
};

export type DropdownTriggerVariant = "none" | "tertiary" | "secondary";

type ListboxShellProps = {
	inverse?: boolean;
	id: string;
	role: "listbox" | "dialog";
	class: string;
	"aria-label"?: string;
	placement: string;
	style: Record<string, string>;
	onKeyDown?: JSX.EventHandlerUnion<HTMLDivElement, KeyboardEvent>;
	ref?: (el: HTMLDivElement) => void;
};

const ListboxShell: ParentComponent<ListboxShellProps> = (props) => {
	const attrs = () => ({
		id: props.id,
		class: props.class,
		role: props.role,
		"aria-label": props["aria-label"],
		"data-placement": props.placement,
		style: props.style,
		onKeyDown: props.onKeyDown,
	});

	return (
		<Show
			when={props.inverse}
			fallback={
				<div ref={props.ref} {...attrs()}>
					{props.children}
				</div>
			}
		>
			<Appearance scheme="inverse" ref={props.ref} {...attrs()}>
				{props.children}
			</Appearance>
		</Show>
	);
};

type DropdownProps = {
	/** Option list for select mode. Ignored when `content` is set. */
	options?: (DropdownOption | DividerOption)[];
	/**
	 * Custom panel body (e.g. forms, rich panels). When set, the portal renders
	 * this instead of the options listbox. A function receives `close` so the
	 * panel can dismiss itself after a choice.
	 */
	content?:
		| JSX.Element
		| ((api: { close: () => void; isOpen: () => boolean }) => JSX.Element);
	value?: string | number;
	onChange?: (value: string | number) => void;
	placeholder?: string;
	disabled?: boolean;
	id?: string;
	"aria-label"?: string;
	"aria-labelledby"?: string;
	class?: string;
	trigger?: JSX.Element;
	/**
	 * Visual variant for a custom `trigger` button.
	 * - `"tertiary"` (default): bordered icon button
	 * - `"secondary"`: monochrome secondary square (matches header action buttons)
	 * - `"none"`: unstyled wrapper around the trigger content
	 */
	triggerVariant?: DropdownTriggerVariant;
	/**
	 * Size of an icon-only tertiary `trigger`. Unset is the compact size for row
	 * menus; `"medium"` matches an icon-only `Button` (2rem), for toolbars.
	 */
	triggerSize?: "medium";
	/** Inline styles applied to the trigger button. */
	triggerStyle?: JSX.CSSProperties;
	/** Hover/focus tip; the whole trigger button is the tooltip anchor. */
	triggerTooltip?: JSX.Element;
	style?: JSX.CSSProperties;
	placement?: DropdownPlacement;
	listBoxStyle?: JSX.CSSProperties;
	/** Portaled listbox is an {@link Appearance} inverse island. */
	inverse?: boolean;
};

/**
 * Select menu for choosing a single option from a list, or a trigger that opens
 * a custom panel when `content` is provided.
 *
 * [Design System →](http://localhost:5174/component/Dropdown)
 */
export const Dropdown: Component<DropdownProps> = (props) => {
	const [panelOpen, setPanelOpen] = createSignal(false);
	let triggerEl: HTMLElement | undefined;
	let panelListboxEl: HTMLDivElement | undefined;

	const placement = () => props.placement ?? "bottom";
	const isSelectCombobox = () => !isContentPanel();
	const triggerVariant = () => props.triggerVariant ?? "tertiary";
	const options = () => props.options ?? [];
	const isContentPanel = () => props.content !== undefined;

	const dropdownId =
		props.id ?? `dropdown-${Math.random().toString(36).substr(2, 9)}`;
	const buttonId = `${dropdownId}-button`;

	const selectedIndex = createMemo(() => {
		if (props.value === undefined) return -1;
		return options().findIndex(
			(opt) => !isDivider(opt) && opt.value === props.value,
		);
	});

	const combobox = useSelectOnlyCombobox({
		id: dropdownId,
		disabled: () => props.disabled || isContentPanel(),
		optionCount: () => options().length,
		selectedIndex,
		getOptionLabel: (index) => {
			const opt = options()[index];
			return opt && !isDivider(opt) ? opt.label : "";
		},
		isOptionDisabled: (index) => {
			const opt = options()[index];
			if (opt == null || isDivider(opt)) {
				return true;
			}
			return Boolean(opt.disabled);
		},
		onSelect: (index) => {
			const opt = options()[index];
			if (opt != null && !isDivider(opt)) {
				props.onChange?.(opt.value);
			}
		},
		placement,
	});

	const isOpen = () => (isContentPanel() ? panelOpen() : combobox.isOpen());
	const listboxId = () => combobox.listboxId;

	const panelPosition = useAnchoredPopupPosition({
		isOpen: () => isContentPanel() && panelOpen(),
		placement,
		getTriggerEl: () => triggerEl,
		getListboxEl: () => panelListboxEl,
	});

	const setTriggerRef = (el: HTMLElement | undefined) => {
		triggerEl = el;
		combobox.setTriggerEl(el);
	};

	const panelContent = () => {
		const content = props.content;
		return typeof content === "function"
			? content({
					close: () => setPanelOpen(false),
					isOpen: () => panelOpen(),
				})
			: content;
	};

	createEffect(() => {
		if (props.disabled) {
			setPanelOpen(false);
		}
	});

	const selectedOption = createMemo(() => {
		const index = selectedIndex();
		const opts = options();
		if (index >= 0 && index < opts.length && !isDivider(opts[index])) {
			return opts[index];
		}
		return undefined;
	});

	const getSelectedLabel = () =>
		selectedOption()?.label ?? props.placeholder ?? "Select an option";

	const selectPopupStyle = (): Record<string, string> => {
		const pos = combobox.popupPosition();
		return {
			...(props.listBoxStyle ?? {}),
			top: pos?.top ?? "0px",
			left: pos?.left ?? "0px",
			minWidth: pos?.minWidth ?? "100px",
		} as Record<string, string>;
	};

	const panelPopupStyle = (): Record<string, string> => {
		const pos = panelPosition.position();
		return {
			...(props.listBoxStyle ?? {}),
			top: pos?.top ?? "0px",
			left: pos?.left ?? "0px",
			minWidth: pos?.minWidth ?? "100px",
		} as Record<string, string>;
	};

	const handlePanelClick = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		if (!props.disabled) {
			setPanelOpen((open) => !open);
		}
	};

	const handlePanelKeyDown: JSX.EventHandlerUnion<
		HTMLElement,
		KeyboardEvent
	> = (event) => {
		if (props.disabled) {
			return;
		}
		const e = event;
		switch (e.key) {
			case "Enter":
			case " ":
				if (!panelOpen()) {
					e.preventDefault();
					setPanelOpen(true);
				}
				break;
			case "Escape":
				e.preventDefault();
				setPanelOpen(false);
				triggerEl?.focus();
				break;
			case "ArrowDown":
			case "ArrowUp":
				if (!panelOpen()) {
					e.preventDefault();
					setPanelOpen(true);
				}
				break;
			case "Tab":
				setPanelOpen(false);
				break;
		}
	};

	const handlePanelClickOutside = (event: MouseEvent) => {
		const path = event.composedPath();
		const dropdown = document.getElementById(dropdownId);
		if (
			(dropdown && path.includes(dropdown)) ||
			(panelListboxEl && path.includes(panelListboxEl))
		) {
			return;
		}
		setPanelOpen(false);
	};

	createEffect(() => {
		if (!panelOpen()) {
			return;
		}
		document.addEventListener("mousedown", handlePanelClickOutside);
		const handleDocumentKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				setPanelOpen(false);
				triggerEl?.focus();
			}
		};
		document.addEventListener("keydown", handleDocumentKeyDown);
		onCleanup(() => {
			document.removeEventListener("mousedown", handlePanelClickOutside);
			document.removeEventListener("keydown", handleDocumentKeyDown);
		});
	});

	const triggerButton = () => (
		<Dynamic
			component={isSelectCombobox() ? "div" : "button"}
			id={buttonId}
			ref={setTriggerRef}
			type={isSelectCombobox() ? undefined : "button"}
			tabIndex={isSelectCombobox() ? (props.disabled ? -1 : 0) : undefined}
			class={
				props.trigger
					? "gvtui-dropdown__button--trigger"
					: "gvtui-dropdown__button"
			}
			classList={{
				[`gvtui-dropdown__button--trigger-${triggerVariant()}`]:
					props.trigger !== undefined && triggerVariant() !== "none",
			}}
			style={props.trigger ? props.triggerStyle : undefined}
			{...(isSelectCombobox() ? combobox.triggerProps() : {})}
			aria-haspopup={isContentPanel() ? "dialog" : undefined}
			aria-expanded={isOpen()}
			aria-controls={listboxId()}
			aria-label={props["aria-label"]}
			aria-labelledby={props["aria-labelledby"]}
			aria-disabled={isSelectCombobox() && props.disabled ? true : undefined}
			disabled={isSelectCombobox() ? undefined : props.disabled}
			onClick={isContentPanel() ? handlePanelClick : combobox.onTriggerClick}
			onKeyDown={
				isContentPanel() ? handlePanelKeyDown : combobox.onTriggerKeyDown
			}
		>
			{props.trigger ? (
				props.trigger
			) : (
				<>
					<span class="gvtui-dropdown__button-content">
						<Show when={selectedOption()?.symbol}>
							{(symbol) => <MaterialSymbol symbol={symbol()} />}
						</Show>
						<span class="gvtui-dropdown__button-text">
							{getSelectedLabel()}
						</span>
					</span>
					<MaterialSymbol
						symbol="keyboard_arrow_down"
						color="fg2"
						class={`gvtui-dropdown__icon ${isOpen() ? "gvtui-dropdown__icon--open" : ""}`}
					/>
				</>
			)}
		</Dynamic>
	);

	return (
		<div
			id={dropdownId}
			ref={combobox.setRootEl}
			class={`gvtui-dropdown ${props.class ?? ""}`}
			style={props.style ? props.style : undefined}
			classList={{
				"gvtui-dropdown--open": isOpen(),
				"gvtui-dropdown--disabled": props.disabled,
				"gvtui-dropdown--has-trigger": props.trigger !== undefined,
				"gvtui-dropdown--trigger-medium": props.triggerSize === "medium",
			}}
		>
			<Show when={props.triggerTooltip} fallback={triggerButton()}>
				<TooltipTrigger
					class="gvtui-dropdown__trigger-tooltip"
					disabled={props.disabled}
				>
					{triggerButton()}
					<Tooltip>{props.triggerTooltip}</Tooltip>
				</TooltipTrigger>
			</Show>
			<Show when={isOpen()}>
				<Portal>
					<Show
						when={isContentPanel()}
						fallback={
							<ListboxShell
								inverse={props.inverse}
								ref={(el) => combobox.setListboxEl(el)}
								class="gvtui-dropdown__listbox"
								aria-label={props["aria-label"]}
								placement={combobox.popupPosition()?.placement ?? placement()}
								style={selectPopupStyle() as Record<string, string>}
								{...combobox.listboxProps()}
							>
								<For each={options()}>
									{(option, index) =>
										isDivider(option) ? (
											<div class="divider" role="presentation" />
										) : (
											// biome-ignore lint/a11y/useFocusableInteractive: APG select-only combobox; focus stays on the combobox via aria-activedescendant.
											// biome-ignore lint/a11y/useKeyWithClickEvents: keyboard navigation is handled on the combobox.
											<div
												class="gvtui-dropdown__option"
												classList={{
													"gvtui-dropdown__option--danger":
														option.theme === "danger",
													"gvtui-dropdown__option--selected":
														index() === selectedIndex(),
													"gvtui-dropdown__option--focused":
														index() === combobox.focusedIndex(),
													"gvtui-dropdown__option--disabled": option.disabled,
												}}
												{...combobox.getOptionProps(index())}
											>
												<Show when={option.symbol}>
													<MaterialSymbol symbol={option.symbol} />
												</Show>
												<div class="gvtui-dropdown__option-body">
													{option.label}
													<Show when={option.description}>
														<div class="gvtui-dropdown__option-description">
															{option.description}
														</div>
													</Show>
												</div>
												<Show when={option.cost}>
													{(cost) => (
														<span class="gvtui-dropdown__option-cost">
															<MaterialSymbol symbol="toll" color="fg4" />
															<span>{cost()}</span>
														</span>
													)}
												</Show>
												<Show when={index() === selectedIndex()}>
													<MaterialSymbol symbol="check" color="primary" />
												</Show>
											</div>
										)
									}
								</For>
							</ListboxShell>
						}
					>
						<ListboxShell
							inverse={props.inverse}
							id={listboxId()}
							ref={(el) => {
								panelListboxEl = el;
							}}
							class="gvtui-dropdown__listbox gvtui-dropdown__listbox--panel"
							role="dialog"
							aria-label={props["aria-label"]}
							placement={panelPosition.position()?.placement ?? placement()}
							style={panelPopupStyle()}
						>
							{panelContent()}
						</ListboxShell>
					</Show>
				</Portal>
			</Show>
		</div>
	);
};
